import React, { useState, useEffect } from "react";
import Popup from "reactjs-popup";
import ATLCodes from "../../data/atlCodes.json";
import { authFetch } from "../../utils/api";

/**
 * Registration prompt shown to a logged-in student who reaches the project
 * submission form without being a registered Tinkerpreneur participant.
 *
 * A variant of ATLTinkerpreneurRegistrationPopup (the Dashboard one) rather than a
 * reuse of it, because the two contexts need different behaviour:
 *
 *   - the Dashboard popup is an invitation, opens itself on mount, and navigates to
 *     the Tinkerpreneur page once done;
 *   - this one is a recovery step for someone already mid-task, so it is opened on
 *     demand, keeps the student on the submission page, and hands control back via
 *     onRegistered so the form can unlock in place.
 *
 * It also resolves the student's state up-front, so ATL code validation has the data
 * it needs on the first attempt.
 */
const ATLTinkerpreneurRegistrationPrompt = ({
  year,
  open,
  onClose,
  onRegistered,
}) => {
  const [filledATLInfo, setFilledATLInfo] = useState(false);
  const [haveAtlLab, setHaveAtlLab] = useState("");
  const [atlCode, setAtlCode] = useState("");
  const [studentState, setStudentState] = useState("");
  const [showError, setShowError] = useState(false);
  const [invalidAtlCode, setInvalidAtlCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Resolve everything the form needs before the student can press Submit, so the
  // ATL code check is not racing an in-flight request the way the Dashboard one is.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    authFetch(`${apiUrl}/atl-tinkerpreneur/has-registered`)
      .then((res) => res.json())
      .then((result) => {
        if (!cancelled) setFilledATLInfo(Boolean(result.filledATLInfo));
      })
      .catch(() => {});

    const studentId = localStorage.getItem("studentId");
    if (studentId) {
      authFetch(`${apiUrl}/users/students/${studentId}`)
        .then((res) => res.json())
        .then((result) => {
          const student = result && result.data && result.data[0];
          if (!cancelled && student) setStudentState(student.state || "");
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [open, apiUrl]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("body-no-scroll");
    } else {
      document.body.classList.remove("body-no-scroll");
    }
    return () => document.body.classList.remove("body-no-scroll");
  }, [open]);

  const register = () => {
    setSubmitFailed(false);

    if (!filledATLInfo) {
      if (haveAtlLab === "" || (haveAtlLab === "yes" && atlCode === "")) {
        setShowError(true);
        return;
      }

      if (haveAtlLab === "yes") {
        const acceptedAtlCodes = ATLCodes[studentState] || [];
        if (!acceptedAtlCodes.includes(atlCode)) {
          setInvalidAtlCode(true);
          setShowError(true);
          return;
        }
      }
    }

    setSubmitting(true);

    // Only send the ATL fields when we actually collected them. The API falls back to
    // the student's profile when they are absent, but an empty string is a value and
    // would overwrite what is already on file.
    const payload = filledATLInfo ? { year } : { year, haveAtlLab, atlCode };

    fetch(`${apiUrl}/atl-tinkerpreneur/enroll`, {
      method: "POST",
      headers: {
        "x-access-token": localStorage.getItem("jwtToken"),
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      // NOTE: the key is haveAtlLab — the API reads req.body.haveAtlLab. Sending any
      // other name silently drops the student's answer.
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`enroll failed: ${response.status}`);
        return response.json();
      })
      .then(() => {
        setSubmitting(false);
        onRegistered();
      })
      .catch(() => {
        // Surfaced to the student — a silent failure here is what left people
        // believing they had registered when they had not.
        setSubmitting(false);
        setSubmitFailed(true);
      });
  };

  if (!open) return null;

  return (
    <Popup
      trigger={<button style={{ display: "none" }}></button>}
      position="top center"
      open={open}
      closeOnDocumentClick={false}
      overlayStyle={{
        backgroundColor: "rgba(0, 0, 0, 0.80)",
        overflow: "hidden",
      }}
    >
      <div
        className="d-flex flex-column p-4 p-lg-5 rounded-3 shadow-lg"
        style={{ backgroundColor: "white" }}
      >
        <h2 className="mt-3 text-center">India AI Tinkerpreneur {year}</h2>
        <p className="text-center mb-2">
          <strong>You are one step away from completing your enrolment.</strong>
          <br />
          Finish it here and submit your project right away.
        </p>

        {!filledATLInfo && (
          <div className="row mt-sm-3 p-0 px-lg-5">
            <div className="form-group col-12 mb-3">
              <label className="d-block mb-2">
                Does your school have ATL Lab?
              </label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="promptHasAtlLab"
                  value="yes"
                  id="promptAtlLabYes"
                  onChange={(e) => setHaveAtlLab(e.target.value)}
                  required
                />
                <label className="form-check-label" htmlFor="promptAtlLabYes">
                  Yes
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="promptHasAtlLab"
                  value="no"
                  id="promptAtlLabNo"
                  onChange={(e) => setHaveAtlLab(e.target.value)}
                  required
                />
                <label className="form-check-label" htmlFor="promptAtlLabNo">
                  No
                </label>
              </div>
              <div className="text-danger">
                {showError && haveAtlLab === "" && "Please choose an option."}
              </div>
            </div>

            {haveAtlLab === "yes" && (
              <div className="form-group col-12 mb-3">
                <label className="d-block mb-2">
                  {`ATL Code (`}
                  <small>
                    <a
                      href={`/assets/pdf/OperationalATLsInIndia.pdf`}
                      style={{ color: "#0048ff" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click here
                    </a>
                    <span>&nbsp;to know ATL code!</span>
                  </small>
                  {`)`}
                </label>
                <input
                  onChange={(e) => {
                    setAtlCode(e.target.value);
                    setInvalidAtlCode(false);
                  }}
                  type="text"
                  name="promptAtlCode"
                  className="form-control"
                  placeholder="ATL Code"
                />
                <small className="text-danger">
                  {showError && atlCode === "" && "ATL Code is required"}{" "}
                  {invalidAtlCode &&
                    showError &&
                    "Invalid ATL Code. Please enter a valid ATL Code."}
                </small>
              </div>
            )}
          </div>
        )}

        {submitFailed && (
          <div className="alert alert-danger mt-2 mb-0">
            We could not complete your registration just now. Please check your
            connection and try again.
          </div>
        )}

        <div className="d-block text-center d-sm-flex justify-content-center w-100 gap-sm-3 mt-sm-3">
          <button
            onClick={register}
            disabled={submitting}
            className="btn btn-primary rounded-3 px-4 py-2 text-white mb-2 mb-sm-0 mr-0 mr-sm-2"
            style={{ minWidth: "140px" }}
          >
            {submitting ? "Enrolling…" : "Complete enrolment"}
          </button>

          <button
            onClick={onClose}
            disabled={submitting}
            className="btn btn-secondary rounded-3 px-4 py-2"
            style={{ minWidth: "140px" }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default ATLTinkerpreneurRegistrationPrompt;
